const statusLabels = {
  '-50': '已拒绝', '-10': '草稿', '0': '审批中', '10': '办理中', '50': '已完成',
  draft: '草稿', pending: '待确认', pending_finance: '待财务定额', pending_special: '待超额特批',
  pending_pricing: '待采购定价', pending_payment: '待收货', pending_receipt: '待收货', executing: '执行中',
  in_transit: '已付款在途', partially_received: '部分收货', received: '已收货',
  partially_allocated: '部分分配', completed: '已完成', open: '未结', partial: '部分结清', settled: '已结清',
  exported: '已导出', processed: '已处理'
}

/** 各页签常驻展示的业务用途和主流程，与右上角详细操作帮助相互补充。 */
export const goldTabGuides = {
  accounts: { title: '库存工作台', summary: '按公司、部门和材质查看本地金料账户及已付款在途。', flow: ['选择公司', '启用金料账户', '核对实物/占用/可用', '查看在途或调整'] },
  demands: { title: '需求与定额', summary: '从销售补料或供应商结算来源提交金料需求，并跟踪审批和执行。', flow: ['新建需求', '财务定额', '超额特批', '采购定价/内部调拨', '收货分配'] },
  purchases: { title: '采购与回货', summary: '跟踪外采金料的付款、在途、分批收货及部门入账进度。', flow: ['关联付款并定价', '登记实际收货', '自动匹配申请部门', '确认分配', '完成/短收关闭'] },
  transfers: { title: '内部调拨', summary: '处理公司总库向需求部门的金料实物交接与双边过账。', flow: ['财务拆分调拨量', '生成调拨单', '完成实物交接', '确认双边过账'] },
  debts: { title: '供应商结余/欠料', summary: '按供应商、部门和材质跟踪欠料与结余，并从部门库办理还料。', flow: ['采购产生欠料', '查看未结头寸', '部门库备料', '送厂还料', '部分/全部结清'] },
  corrections: { title: '成本修正', summary: '核对欠料预估价与实际还料价产生的成本差异及调账状态。', flow: ['生成成本差异', '选择会计月份', '导出月报', '财务调账', '登记凭证'] }
}

/** 用同一批行数据计算汇总卡和下钻明细，保证弹框合计与卡片一致。 */
export function goldSummaryCards(workbench = {}, debts = []) {
  const accounts = workbench.accounts || []
  const transit = workbench.transit || []
  const openDebts = (debts || []).filter(item => ['open', 'partial'].includes(item.status))
  const sum = (rows, key) => rows.reduce((total, row) => total + Number(row[key] || 0), 0)
  return [
    { key: 'inventoryQuantity', label: '实物库存', value: sum(accounts, 'quantity'), precision: 4, suffix: 'g', detailType: 'accounts', rows: accounts,
      description: '计算逻辑：当前公司各金料账户的账面实物克重之和（Σ 实物克重）。采购到货只有完成部门分配后才计入；在途、已收货但待分配、供应商结余或欠料均不计入。' },
    { key: 'availableQuantity', label: '可用库存', value: sum(accounts, 'availableQuantity'), precision: 4, suffix: 'g', detailType: 'accounts', rows: accounts,
      description: '计算逻辑：当前公司各金料账户的可用克重之和；单个账户可用克重 = 实物克重 - 已占用克重。已被货品采购冻结的克重仍属于实物库存，但不可再次使用。' },
    { key: 'inventoryAmount', label: '库存值', value: sum(accounts, 'inventoryAmount'), precision: 2, suffix: '元', detailType: 'accounts', rows: accounts,
      description: '计算逻辑：当前公司各金料账户库存金额之和（Σ 账户库存金额）。账户入库、调拨及扣减按移动加权平均成本记账；在途值和供应商结余或欠料金额不计入库存值。' },
    { key: 'transitQuantity', label: '在途克重', value: sum(transit, 'transitQuantity'), precision: 4, suffix: 'g', detailType: 'transit', rows: transit,
      description: '计算逻辑：仅统计付款流程已完成（状态 50）且仍有未收货量的金料采购；单笔在途克重 = 采购克重 - 已收货克重 - 短收关闭克重。采购列表的“待收货”只表示已关联付款单并可收货，付款未完成时不计入已付款在途。' },
    { key: 'transitAmount', label: '在途值', value: sum(transit, 'transitAmount'), precision: 2, suffix: '元', detailType: 'transit', rows: transit,
      description: '计算逻辑：已付款在途明细金额之和；单笔在途值 =（采购克重 - 已收货克重 - 短收关闭克重）× 实际金价，单笔金额保留两位小数。付款未完成的待收货采购不计入。' },
    { key: 'unsettledQuantity', label: '未结欠料', value: sum(openDebts, 'unsettledQuantity'), precision: 4, suffix: 'g', detailType: 'debts', rows: openDebts,
      description: '计算逻辑：状态为“未结”或“部分结清”的供应商欠料，其剩余未结克重之和。欠料表示尚需向供应商归还的金料，不属于本地实物库存或在途。' }
  ]
}

/** 金料工作台所有状态使用同一中文名称，历史 pending_payment 兼容显示为待收货。 */
export function statusText(value) {
  return statusLabels[String(value)] || value || '-'
}

/** 付款审批和金额差异只作为核对信息，不再阻止已关联采购的实物操作。 */
export function paymentNotice(bill) {
  const paymentID = bill.paymentFlowInstanceID || bill.demandPaymentFlowInstanceID
  if (!paymentID) return '尚未关联采购付款请款申请单。'
  if (bill.paymentStatus === null || bill.paymentStatus === undefined) return '已关联付款单，但当前无法读取审批状态；不影响收货或短收关闭。'
  const messages = []
  if (bill.paymentAmount === null || bill.paymentAmount === undefined) {
    messages.push('关联付款单金额无效，请核对')
  } else if (bill.purchaseTotalAmount !== null && bill.purchaseTotalAmount !== undefined) {
    const difference = Math.round((Number(bill.purchaseTotalAmount) - Number(bill.paymentAmount)) * 100) / 100
    if (difference !== 0) messages.push(`采购总额与付款金额相差 ${difference.toFixed(2)} 元，请核对`)
  }
  messages.push(Number(bill.paymentStatus) === 50 ? '付款审批已完成' : `付款状态：${statusText(bill.paymentStatus)}`)
  return `${messages.join('；')}。付款审批和金额差异不影响收货或短收关闭。`
}

/** 实物操作只要求已绑定付款单和完整定价，不校验付款审批状态或金额差异。 */
export function purchaseOperationBlock(bill) {
  const paymentID = bill.paymentFlowInstanceID || bill.demandPaymentFlowInstanceID
  if (!paymentID) return '尚未关联采购付款请款申请单，不能收货或短收关闭。'
  if (bill.pricedQuantity !== null && bill.pricedQuantity !== undefined &&
      bill.totalGoldQuantity !== null && bill.totalGoldQuantity !== undefined &&
      Number(bill.pricedQuantity) !== Number(bill.totalGoldQuantity)) return '采购明细尚未完成全部定价。'
  return ''
}

// 保留旧导出名称供现有调用兼容；其语义已调整为非阻断付款提示。
export const paymentBlock = paymentNotice

export function demandActions(bill, access) {
  const actions = []
  const add = (key, label, tip) => actions.push({ key, label, tip })
  if (bill.status === 'draft' && access.request) add('submit', '提交', '确认需求后提交财务确定批准量、内部调拨量和外采量。')
  if (bill.status === 'pending_finance' && access.finance) add('finance', '财务定额', '批准克重必须等于内部调拨与外部采购之和。')
  if (bill.status === 'pending_special' && access.special) add('special', '超额特批', '由财务负责人确认超过系统建议量的备货需求。')
  if (bill.status === 'pending_pricing' && access.price) add('price', '金料定价', '整单外采统一供应商和实际金价，只关联一张采购付款请款单。')
  if (bill.status === 'executing') {
    if (bill.paymentFlowInstanceID) add('payment', '查看付款', paymentNotice(bill))
    if (access.inventory && Number(bill.pendingReceiptQuantity) > 0 && !purchaseOperationBlock(bill)) add('receipt', '收货', '已关联付款单即可按实际到货克重登记，可分批收货。')
    if (access.inventory && Number(bill.pendingAllocationQuantity) > 0) add('allocate', '分配', '将已收货批次分配到需求部门账户后，才增加部门实物库存。')
    if (access.inventory && Number(bill.pendingTransferCount) > 0) add('transfer', '调拨交接', '定位该需求调拨单，确认实物交接后从总库扣减并入部门库。')
  }
  if (bill.status === 'completed' && access.inventory && Number(bill.unsettledDebtCount) > 0) add('dispatch', '供应商还料', '需求已完成备料；继续从部门库送厂还料，单独结清供应商欠料。')
  return actions
}

export function demandGuidance(bill) {
  const descriptions = {
    draft: '下一步：提交需求，由财务确认定额。',
    pending_finance: '下一步：财务确认批准克重，并拆分内部调拨和外采；超额需求还需特批。',
    pending_special: '下一步：财务负责人完成超额特批，再办理调拨或采购定价。',
    pending_pricing: '下一步：对整单外采统一定价并关联采购付款请款单；存在内部调拨时可同时办理交接。',
    completed: Number(bill.unsettledDebtCount) > 0 ? '采购分配与调拨已完成；供应商仍有未结欠料，请继续办理送厂还料。' : '采购分配与内部调拨已完成。供应商还料按欠料台账独立跟踪。'
  }
  if (bill.status !== 'executing') return descriptions[bill.status] || '请查看关联业务单据的处理进度。'
  const steps = []
  if (Number(bill.pendingTransferCount) > 0) steps.push(`待交接调拨 ${bill.pendingTransferCount} 笔`)
  if (Number(bill.pendingReceiptQuantity) > 0) steps.push(`待收货 ${Number(bill.pendingReceiptQuantity).toFixed(4)} g`)
  if (Number(bill.pendingAllocationQuantity) > 0) steps.push(`待分配 ${Number(bill.pendingAllocationQuantity).toFixed(4)} g`)
  return `${steps.join('；') || '正在更新执行进度'}。付款审批与实物执行独立；全部采购分配和调拨交接完成后，需求自动完成。`
}

/** 按业务上下文提供每条状态数据的可操作说明。 */
export function statusTooltip(context, row = {}) {
  const status = String(row.status ?? row.displayStatus ?? '')
  if (context === 'demand') return demandGuidance(row)
  if (context === 'payment') {
    return ({ '-50': '付款流程已拒绝。', '-10': '付款申请仍为草稿。', '0': '付款申请正在审批。', '10': '付款申请正在办理。', '50': '付款流程已经完成。' })[status] || '请打开付款单查看当前审批节点。'
  }
  const maps = {
    purchase: {
      pending_payment: '已关联付款单，可登记收货；付款审批状态单独跟踪。',
      pending_receipt: '已关联付款单，等待实际到货；可直接登记收货或办理短收关闭。',
      in_transit: '付款审批已完成且仍有未收货克重。',
      partially_received: '已登记部分收货，剩余数量可继续收货或短收关闭。',
      received: '采购量已全部收货或短收关闭，已收货部分仍需完成部门分配。',
      partially_allocated: '部分收货批次已进入部门库存，仍有待分配克重。',
      completed: '所有实际收货均已分配，或剩余数量已短收关闭。'
    },
    transfer: { pending: '等待确认公司总库向需求部门完成实物交接。', completed: '调拨已确认，金料已从公司总库过账至部门账户。' },
    debt: { open: '欠料尚未开始归还，可从对应部门库存办理送厂还料。', partial: '已归还部分欠料，剩余未结克重仍需处理。', settled: '欠料已全部归还并完成结清。' },
    correction: { pending: '成本差额待财务导出或处理。', exported: '成本差额已导出，等待登记调账凭证。', processed: '财务已登记凭证并完成成本差额处理。' }
  }
  return maps[context]?.[status] || `当前状态：${statusText(status)}`
}
